import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  HStack,
  IconButton,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { MdContentCopy } from "react-icons/md";
import SetupCheckList from "./SetupCheckList";
import { useDispatch, useSelector } from "react-redux";
import { getChecklists, deleteChecklist, duplicateChecklist } from "../../redux/actions/checklistActions";
import MessageBox from "../../components/helpers/MessageBox";
import { gameConsoleList } from "../../global-client";

const SetupCheckLists = () => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const checklists = useSelector((state) => state.checklist.checklists);
  const dispatch = useDispatch();
  const toast = useToast();

  // Mode colors
  const borderTableColor = useColorModeValue("gray.200", "gray.700");
  const theadBg = useColorModeValue("gray.50", "gray.900");
  const rowSelectedBg = useColorModeValue("blue.50", "gray.800");
  const rowHoverBg = useColorModeValue("gray.50", "gray.800");
  const iconEditHover = useColorModeValue("blue.100", "blue.900");
  const iconDeleteHover = useColorModeValue("red.100", "red.900");
  const emptyBorder = useColorModeValue("gray.300", "gray.600");
  const emptyHeading = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    dispatch(getChecklists());
  }, [dispatch]);

  const handleDuplicate = async (checklistId) => {
    try {
      await dispatch(duplicateChecklist(checklistId));
      dispatch(getChecklists());
      toast({
        title: "Success",
        description: "Checklist duplicated successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to duplicate checklist",
        status: "error",
        duration: 3000,
      });
    }
  };

  const gameConsoles = gameConsoleList();

  const getConsoleName = (consoleId) => {
    if (!consoleId) return "-";
    const console = gameConsoles.find((c) => c.id === consoleId);
    return console ? console.name : "-";
  };

  const getModelName = (consoleId, modelId) => {
    if (!consoleId || !modelId) return "-";
    const console = gameConsoles.find((c) => c.id === consoleId);
    if (!console || !console.models) return "-";
    const model = console.models.find((m) => m.id === modelId);
    return model ? model.name : "-";
  };

  const handleAddNew = () => {
    setEditingChecklist(null);
    setIsAdding(true);
  };

  const handleEdit = (checklist) => {
    setEditingChecklist(checklist);
    setIsAdding(true);
    setSelectedRow(checklist._id);
  };

  const handleDelete = (checklistId) => {
    setDeleteId(checklistId);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteChecklist(deleteId));
      dispatch(getChecklists());
      toast({
        title: "Success",
        description: "Checklist deleted successfully",
        status: "success",
        duration: 3000,
      });
      setDeleteId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete checklist",
        status: "error",
        duration: 3000,
      });
      setDeleteId(null);
    }
  };

  const handleSaveComplete = () => {
    dispatch(getChecklists());
    setIsAdding(false);
    setEditingChecklist(null);
    setSelectedRow(null);
  };

  return (
    <Box p={{ base: 3, md: 6 }}>
      {checklists.length > 0 && !isAdding && (
        <Box>
          <HStack
            mb={6}
            justify='space-between'
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            spacing={{ base: 3, md: 6 }}
          >
            <Heading size={{ base: "md", md: "lg" }} fontWeight='600'>
              Checklists
            </Heading>
            <Button
              leftIcon={<AddIcon />}
              colorScheme='blue'
              size={{ base: "sm", md: "md" }}
              onClick={handleAddNew}
              w={{ base: "100%", md: "auto" }}
            >
              Add Checklist
            </Button>
          </HStack>
          <TableContainer
            borderRadius='lg'
            shadow='sm'
            border='1px'
            borderColor={borderTableColor}
            overflow={{ base: "auto", md: "hidden" }}
            maxW={{ base: "100%", md: "100%" }}
          >
            <Table size={{ base: "sm", md: "md" }} variant='simple'>
              <Thead bg={theadBg}>
                <Tr>
                  <Th fontSize={{ base: "xs", md: "sm" }} fontWeight='600'>
                    Name
                  </Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} fontWeight='600'>
                    Console
                  </Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} fontWeight='600'>
                    Model
                  </Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} fontWeight='600' display={{ base: "none", md: "table-cell" }}>
                    Progress
                  </Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} fontWeight='600' display={{ base: "none", md: "table-cell" }}>
                    Items
                  </Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} fontWeight='600'>
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {checklists.map((checklist) => (
                  <Tr
                    key={checklist._id}
                    bg={selectedRow === checklist._id ? rowSelectedBg : "transparent"}
                    _hover={{ bg: rowHoverBg }}
                    transition='background-color 0.2s'
                  >
                    <Td fontSize={{ base: "xs", md: "sm" }} fontWeight={selectedRow === checklist._id ? "600" : "500"}>
                      {checklist.checklistName}
                    </Td>
                    <Td fontSize={{ base: "xs", md: "sm" }}>
                      <Badge
                        variant='subtle'
                        colorScheme='purple'
                        borderRadius='md'
                        fontSize={{ base: "10px", md: "xs" }}
                      >
                        {getConsoleName(checklist.consoleId)}
                      </Badge>
                    </Td>
                    <Td fontSize={{ base: "xs", md: "sm" }}>
                      {checklist.modelId === "ALL"
                        ? "All Models"
                        : getModelName(checklist.consoleId, checklist.modelId)}
                    </Td>
                    <Td fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", md: "table-cell" }}>
                      <Badge
                        variant='subtle'
                        colorScheme='green'
                        borderRadius='md'
                        fontSize={{ base: "10px", md: "xs" }}
                      >
                        {checklist.progress}
                      </Badge>
                    </Td>
                    <Td fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", md: "table-cell" }}>
                      {checklist.items?.length || 0}
                    </Td>
                    <Td>
                      <HStack spacing={{ base: 0.5, md: 1 }}>
                        <IconButton
                          icon={<EditIcon />}
                          size={{ base: "xs", md: "sm" }}
                          variant='ghost'
                          colorScheme='blue'
                          onClick={() => handleEdit(checklist)}
                          aria-label='Edit checklist'
                          _hover={{ bg: iconEditHover }}
                        />
                        <IconButton
                          icon={<MdContentCopy />}
                          size={{ base: "xs", md: "sm" }}
                          variant='ghost'
                          colorScheme='teal'
                          onClick={() => handleDuplicate(checklist._id)}
                          aria-label='Duplicate checklist'
                          _hover={{ bg: "teal.100" }}
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size={{ base: "xs", md: "sm" }}
                          variant='ghost'
                          colorScheme='red'
                          onClick={() => handleDelete(checklist._id)}
                          aria-label='Delete checklist'
                          _hover={{ bg: iconDeleteHover }}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {isAdding && (
        <Box>
          <SetupCheckList editingChecklist={editingChecklist} onSaveComplete={handleSaveComplete} />
        </Box>
      )}

      {!isAdding && checklists.length === 0 && (
        <Box
          textAlign='center'
          py={{ base: 8, md: 16 }}
          px={{ base: 4, md: 0 }}
          borderRadius='lg'
          border='2px dashed'
          borderColor={emptyBorder}
        >
          <Heading size={{ base: "sm", md: "md" }} mb={4} color={emptyHeading}>
            No checklists yet
          </Heading>
          <Button
            leftIcon={<AddIcon />}
            colorScheme='blue'
            size={{ base: "md", md: "lg" }}
            onClick={handleAddNew}
            w={{ base: "100%", md: "auto" }}
          >
            Create Your First Checklist
          </Button>
        </Box>
      )}

      <MessageBox
        isOpen={deleteId}
        onClose={() => setDeleteId(null)}
        onCancel={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title='Delete Checklist'
        description='Are you sure you want to delete this checklist? This action cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        colorScheme='red'
      />
    </Box>
  );
};

export default SetupCheckLists;
